import { graphql } from '@mp/graphql/generated';
import { useCurrentUser } from '@mp/hooks';
import { type ResultOf } from '@graphql-typed-document-node/core';
import { useGraphQLQuery } from '@mp/hooks/useGraphQLRequest';

const AllPositionsGql = graphql(`
  query GetAllPositionsForWorkHistoryForm($input: PositionQueryInput) {
    positions(input: $input) {
      id
      display_name
    }
  }
`);

const LocationTypesGql = graphql(`
  query GetLocationTypesForWorkHistoryForm($input: LocationTypeQueryInput) {
    locationTypes(input: $input) {
      id
      label
    }
  }
`);

const StatesGql = graphql(`
  query GetStatesForWorkHistoryForm($input: StateQueryInput) {
    states(input: $input) {
      id
      abbreviation
    }
  }
`);

export const AssignmentRequestsGql = graphql(`
  query GetAssignmentRequestsForWorkHistoryForm($input: AssignmentRequestQueryInput) {
    assignmentRequests(input: $input) {
      id
    }
  }
`);

export const useAllPositionsForWorkHistoryModal = (allKindsPositions = false, enabled = true) => {
  const { currentUser } = useCurrentUser();
  const filterByKinds = allKindsPositions ? undefined : currentUser?.professional?.kinds;
  const queryKey = ['positions', filterByKinds];
  const queryEnabled = enabled && !!currentUser?.professional?.kinds;

  const { data, isLoading } = useGraphQLQuery<ResultOf<typeof AllPositionsGql>>({
    operationName: 'GetAllPositionsForWorkHistoryForm',
    queryKey,
    query: AllPositionsGql,
    variables: {
      input: {
        filter: { active: true },
        search: { kinds: filterByKinds },
        orderBy: { display_name: true },
      },
    },
    enabled: queryEnabled,
  });

  const allPositions = data?.positions || [];

  return {
    allPositions,
    isLoading: queryEnabled ? isLoading : false,
  };
};

export const useLocationTypes = (enabled = true) => {
  const queryKey = ['location_types'];

  const { data, isLoading } = useGraphQLQuery<ResultOf<typeof LocationTypesGql>>({
    operationName: 'GetLocationTypesForWorkHistoryForm',
    queryKey,
    query: LocationTypesGql,
    variables: {
      input: {
        filter: {
          active: true,
        },
        orderBy: {
          label: true,
        },
      },
    },
    enabled,
  });

  const locationTypes = data?.locationTypes || [];

  return { isLoading: enabled ? isLoading : false, locationTypes };
};

export const useStates = () => {
  const { data, isLoading } = useGraphQLQuery<ResultOf<typeof StatesGql>>({
    operationName: 'GetStatesForWorkHistoryForm',
    queryKey: ['states'],
    query: StatesGql,
    variables: { input: { orderBy: { name: true } } },
  });

  const states = data?.states || [];

  return {
    isLoading,
    states,
  };
};

export const useAssignmentRequestsForWorkHistoryModal = (assignmentId: number) => {
  const { currentUser } = useCurrentUser();

  const assignment_id = assignmentId;
  const professional_id = currentUser?.professional?.id;
  const enabled = assignment_id === undefined ? true : !!assignment_id;

  const { data, isLoading } = useGraphQLQuery<ResultOf<typeof AssignmentRequestsGql>>({
    operationName: 'GetAssignmentRequestsForWorkHistoryForm',
    queryKey: ['assignmentRequests', assignment_id, professional_id],
    query: StatesGql,
    enabled,
    variables: {
      input: { filter: { assignment_id }, orderBy: { updated_at: false } },
      professional_id,
    },
  });

  const assignmentRequests = data?.assignmentRequests || [];

  return {
    isLoading,
    assignmentRequests,
  };
};
